import React, { useState, useRef, useEffect } from 'react';
import QrScanner from 'qr-scanner';

interface QRScannerProps {
  onScanSuccess: (token: string) => void;
  onScanError: (error: string) => void;
  isActive: boolean;
}

export const QRScanner: React.FC<QRScannerProps> = ({ onScanSuccess, onScanError, isActive }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [qrScanner, setQrScanner] = useState<QrScanner | null>(null);
  const [hasCamera, setHasCamera] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    if (!videoRef.current) return;

    // Check if camera is available
    QrScanner.hasCamera().then(setHasCamera);

    if (isActive && hasCamera) {
      const scanner = new QrScanner(
        videoRef.current,
        (result) => {
          setIsScanning(false);
          onScanSuccess(result.data);
        },
        {
          onDecodeError: (error) => {
            // Don't spam errors for normal decode failures
            console.debug('QR Decode:', error);
          },
          highlightScanRegion: true,
          highlightCodeOutline: true,
          maxScansPerSecond: 3,
        }
      );

      setQrScanner(scanner);
      scanner.start().then(() => {
        setIsScanning(true);
      }).catch((error) => {
        console.error('Scanner start error:', error);
        onScanError('Failed to start camera: ' + error.message);
      });

      return () => {
        scanner.stop();
        scanner.destroy();
        setQrScanner(null);
        setIsScanning(false);
      };
    }
  }, [isActive, hasCamera, onScanSuccess, onScanError]);

  if (!hasCamera) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-6xl mb-4">📱</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Câmera Necessária</h3>
        <p className="text-sm text-gray-600 text-center max-w-sm">
          Para escanear códigos QR, é necessário acesso à câmera do dispositivo.
          Certifique-se de que a câmera está habilitada.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      <video
        ref={videoRef}
        className="w-full h-64 bg-black rounded-lg object-cover"
        muted
        playsInline
      />
      
      {isScanning && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="border-2 border-white rounded-lg w-48 h-48 opacity-50"></div>
        </div>
      )}
      
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
        <div className="bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm">
          {isScanning ? 'Escaneando QR Code...' : 'Preparando câmera...'}
        </div>
      </div>
    </div>
  );
};